import React, { useState } from 'react';
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ArrowLeft, Plus, Trash2, Edit, Save } from 'lucide-react';
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const ProjectDetailsForm = ({ account, project, onBack, onSave, axiosInstance, currentUser }) => {
  // Helper Functions - Defined at the top to prevent reference errors
  const parseNumber = (val) => {
    if (!val) return 0;
    const str = val.toString().replace(/[^0-9.-]/g, '');
    return parseFloat(str) || 0;
  };

  const formatCurrency = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatQuantity = (num) => {
    // Integer format, no decimals, with commas
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const cleanCurrency = (val) => {
    if (!val) return 0;
    // If it's already a number, return it
    if (typeof val === 'number') return val;
    // Remove non-numeric characters except dot and minus
    const str = val.toString().replace(/[^0-9.-]/g, '');
    return parseFloat(str) || 0;
  };

  const handleCurrencyBlur = (setter, value) => {
      const num = parseNumber(value);
      if (num || num === 0) {
          setter(formatCurrency(num));
      }
  };

  const handleCurrencyFocus = (setter, value) => {
      if (value) {
          setter(value.toString().replace(/,/g, ''));
      }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Determine if form should be read-only for regular admins
  // Trigger: if "ACTUAL PROJECT COST" has a value (> 0)
  const isSuperAdmin = currentUser?.is_super_admin;
  const currentUserEmail = (currentUser?.email || '').trim().toLowerCase();
  const isCadfUser = currentUserEmail === 'cadf@isourceasiaworkplace.com';
  const projectActualCost = project?.actual_project_cost 
    ? parseFloat(project.actual_project_cost.toString().replace(/[^0-9.-]/g, '')) 
    : 0;
  const isReadOnly = !isSuperAdmin && !isCadfUser && projectActualCost > 0;

  const [items, setItems] = useState([]); // Initialize empty

  // Header State
  const [pccNumber, setPccNumber] = useState('');
  const [dateSubmitted, setDateSubmitted] = useState('');
  const [dateNeeded, setDateNeeded] = useState('');
  const [isDateSubmittedFocused, setIsDateSubmittedFocused] = useState(false);
  const [isDateNeededFocused, setIsDateNeededFocused] = useState(false);
  const [projectName, setProjectName] = useState(''); // New State for Project Name
  const [projectType, setProjectType] = useState('');
  const [endUser, setEndUser] = useState('');
  const [bdoName, setBdoName] = useState('');
  const [totalProjectCost, setTotalProjectCost] = useState('');
  const [actualProjectCost, setActualProjectCost] = useState('');
  const [collectionAmount, setCollectionAmount] = useState('');
  // const [fixedAmount, setFixedAmount] = useState(''); // Fixed/Dictated Amount - Removed
  const [fixedAmount2, setFixedAmount2] = useState(''); // Fixed/Dictated Amount 2
  const [notes, setNotes] = useState('');
  
  // Approval State
  const [isApproved, setIsApproved] = useState(false);
  const [approvedAt, setApprovedAt] = useState(null);
  const [isApprovedPricing, setIsApprovedPricing] = useState(false);
  const [approvedPricingAt, setApprovedPricingAt] = useState(null);
  const [isApprovedCosts, setIsApprovedCosts] = useState(false);
  const [approvedCostsAt, setApprovedCostsAt] = useState(null);
  const [isDisapprovedCosts, setIsDisapprovedCosts] = useState(false);
  const [disapprovedCostsAt, setDisapprovedCostsAt] = useState(null);
  const [isApprovedJoygie, setIsApprovedJoygie] = useState(false);
  const [approvedJoygieAt, setApprovedJoygieAt] = useState(null);
  const [isApprovedOverall, setIsApprovedOverall] = useState(false);
  const [approvedOverallAt, setApprovedOverallAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Inventory Search Modal State
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [isFetchingInventory, setIsFetchingInventory] = useState(false);

  // Marketing Expense State
  const [marketingExpense, setMarketingExpense] = useState({
    retention_rate: 1, // Default to 1%
    cred_tax_1: '',
    cred_tax_5: '',
    retention_amount: '',
    total_deduction: '',
    net_of_collection: '',
    me_rate: '',
    total_me_amount: '',
    manual_total: '', // Manual input for Marketing Expense in Cost Centre
  });

  const [retentionModalOpen, setRetentionModalOpen] = useState(false);
  const [tempRetentionRate, setTempRetentionRate] = useState('');

  const [marketingExpense2, setMarketingExpense2] = useState({
    amount: '',
    vat: '',
    income_tax: '',
    total: ''
  });

  // Cost Sections State - Initialize empty
  const [costCentreItems, setCostCentreItems] = useState([]);
  const [deliveryDetailsItems, setDeliveryDetailsItems] = useState([]);
  const [otherCostItems, setOtherCostItems] = useState([]);
  const [accountingAdminItems, setAccountingAdminItems] = useState([]);

  // Function to generate PCC Number
  const generatePccNumber = async () => {
    // Extensive debugging
    console.log("Attempting to generate PCC Number");
    console.log("Account:", account);
    console.log("Axios Instance:", axiosInstance ? "Available" : "Missing");

    // Robust check for account and email
    let email = account?.email || account?.user_email || account?.bdo_email;
    
    // Fallback to currentUser email if account doesn't have one (e.g. creating new project)
    if (!email && currentUser?.email) {
        console.log("Using currentUser email as fallback:", currentUser.email);
        email = currentUser.email;
    }
    
    if (!email) {
        console.warn("Skipping PCC generation: Email missing from account and currentUser", { account, currentUser });
        return;
    }

    if (!axiosInstance) {
        console.error("Skipping PCC generation: axiosInstance is missing");
        return;
    }

    // 1. Extract Prefix from Email (before @)
    const emailPrefix = email.split('@')[0].toUpperCase();

    // 2. Get Current Date Parts
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // 01-12

    // 3. Construct Prefix for Query
    const queryPrefix = `${emailPrefix}-${year}-${month}`;

    try {
      console.log("Fetching next sequence for prefix:", queryPrefix);
      // 4. Fetch Next Sequence from Backend
      const response = await axiosInstance.get(`/ccp/project-details/sequence?prefix=${queryPrefix}`);
      const sequence = response.data.sequence;

      // 5. Set Full PCC Number
      const newPccNumber = `${queryPrefix}-${sequence}`;
      console.log("Setting generated PCC Number:", newPccNumber);
      setPccNumber(newPccNumber);
      
    } catch (error) {
      console.error("Failed to generate PCC Number:", error);
    }
  };

  // Load data from project prop
  React.useEffect(() => {
    console.log("ProjectDetailsForm Effect Triggered");
    
    try {
      if (project) {
        // PCC Number Logic
        if (project.pcc_number) {
          console.log("Using existing PCC Number:", project.pcc_number);
          setPccNumber(project.pcc_number.toUpperCase());
        } else {
           // New Project
           if (!pccNumber) { 
              console.log("New project detected, triggering generation...");
              generatePccNumber();
           }
        }
        
        // Basic Fields
        setDateSubmitted(project.date_submitted || '');
        setDateNeeded(project.date_needed || '');
        setProjectName(project.project_name || ''); // Added
        setEndUser(project.end_user || '');
        
        // Project Type
        if (project.pcc_number) {
            setProjectType(project.project_type || '');
        } else {
            setProjectType('');
        }
        
        // BDO Name
        // 1. Try project's existing BDO name
        // 2. Try account's BDO name
        // 3. Try current user's full name (if creating/editing by BDO)
        const bdo = project.bdo_name || account?.bdo_name || currentUser?.full_name || '';
        setBdoName(bdo);
  
        // Costs
        setTotalProjectCost(formatCurrency(parseNumber(project.total_project_cost || '')));
        setActualProjectCost(formatCurrency(parseNumber(project.actual_project_cost || '')));
        
        // Collection Amount - use clean format without '₱' to avoid input locking
        setCollectionAmount(formatCurrency(parseNumber(project.collection_amount || '')));
        
        setNotes(project.notes || '');

        // Marketing Expense & Fixed Amount
        const me = project.marketing_expense || {};
        
        setMarketingExpense(prev => ({
            ...prev,
            retention_rate: me.retention_rate !== undefined ? me.retention_rate : 1,
            manual_total: formatCurrency(parseNumber(me.manual_total || ''))
        }));

        setMarketingExpense2({
            amount: formatCurrency(parseNumber(me.amount_2 || '')),
            vat: formatCurrency(parseNumber(me.vat_2 || '')),
            income_tax: formatCurrency(parseNumber(me.income_tax_2 || '')),
            total: formatCurrency(parseNumber(me.total_2 || ''))
        });
        
        // Items
        const formattedItems = (project.items || []).map(item => {
          const qty = parseNumber(item.qty);
          const sellingPrice = parseNumber(item.sellingPrice);
          const opAmount = parseNumber(item.opAmount);
          const totalSellingAmount = parseNumber(item.totalSellingAmount || ((sellingPrice + opAmount) * qty));
          const vatableSales = totalSellingAmount / 1.12;
          const vat = vatableSales * 0.12;
          const invoiceAmount = vatableSales + vat;

          return {
            ...item,
            qty: formatQuantity(qty),
            sellingPrice: formatCurrency(sellingPrice),
            totalSellingPrice: formatCurrency(parseNumber(item.totalSellingPrice || (qty * sellingPrice))),
            opAmount: formatCurrency(opAmount),
            totalOp: formatCurrency(parseNumber(item.totalOp)),
            totalSellingAmount: formatCurrency(totalSellingAmount),
            vatableSales: formatCurrency(parseNumber(item.vatableSales || vatableSales)),
            vat: formatCurrency(parseNumber(item.vat || vat)),
            invoiceAmount: formatCurrency(parseNumber(item.invoiceAmount || invoiceAmount))
          };
        });
  
        setItems(formattedItems);
        
        const formatSectionItems = (items) => (items || []).map(item => ({
          ...item,
          value: (item.value !== null && item.value !== undefined && item.value !== '') 
            ? `₱ ${parseNumber(item.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
            : ''
        }));
  
        setCostCentreItems(formatSectionItems(project.cost_centre_items));
        setDeliveryDetailsItems(formatSectionItems(project.delivery_details_items));
        setOtherCostItems(formatSectionItems(project.other_cost_items));
        setAccountingAdminItems(formatSectionItems(project.accounting_admin_items));
        
        // Approval Status
        setIsApproved(project.is_approved === true);
        setApprovedAt(project.approved_at || null);
        setIsApprovedPricing(project.is_approved_pricing === true);
        setApprovedPricingAt(project.approved_pricing_at || null);
        setIsApprovedCosts(project.is_approved_costs === true);
        setApprovedCostsAt(project.approved_costs_at || null);
        setIsDisapprovedCosts(project.is_disapproved_costs === true);
        setDisapprovedCostsAt(project.disapproved_costs_at || null);
        setIsApprovedJoygie(project.is_approved_joygie === true);
        setApprovedJoygieAt(project.approved_joygie_at || null);
        setIsApprovedOverall(project.is_approved_overall === true);
        setApprovedOverallAt(project.approved_overall_at || null);
      }
    } catch (error) {
      console.error("Error loading project details:", error);
      toast.error("Failed to load project details. Please try again.");
    }
  }, [project, account, axiosInstance, currentUser]);

  // Auto-calculate Actual Project Cost from Items Total Selling Amount
  React.useEffect(() => {
    const total = items.reduce((sum, item) => {
        return sum + parseNumber(item.totalSellingAmount);
    }, 0);
    setActualProjectCost(formatCurrency(total));
  }, [items]);

  // Auto-calculate Marketing Expense (ME) Table
  React.useEffect(() => {
    // 1. Invoice Amount = fetch the value of the total of "Invoice Amount" from items table
    const invoiceAmountTotal = items.reduce((sum, item) => sum + parseNumber(item.invoiceAmount), 0);

    // 2. 1% Cred Tax = "Invoice Amount" value divided by 1.12, multiply by 0.01.
    const credTax1 = (invoiceAmountTotal / 1.12) * 0.01;

    // 3. 5% Cred Tax = "Invoice Amount" value divided by 1.12, multiply by 0.05.
    const credTax5 = (invoiceAmountTotal / 1.12) * 0.05;

    // 4. Retention = "Invoice Amount" value multiply by the % value of Retention.
    const retentionRate = marketingExpense.retention_rate || 0;
    const retentionAmount = invoiceAmountTotal * (retentionRate / 100);

    // 5. Total Deduction = sum up (1% Cred Tax, 5% Cred Tax, Retention)
    const totalDeduction = credTax1 + credTax5 + retentionAmount;

    // 6. Net of Collection = difference of "Invoice Amount" and "Total Deduction"
    const netOfCollection = invoiceAmountTotal - totalDeduction;

    // 7. ME Rate = "Marketing Expense" (from "Cost Centre" table) divided by "Invoice Amount"
    const manualMarketingExpense = parseNumber(marketingExpense.manual_total);
    const meRate = invoiceAmountTotal > 0 ? (manualMarketingExpense / invoiceAmountTotal) : 0;

    // 8. Total ME Amount = Net of Collection multiply by ME Rate
    const totalMeAmount = netOfCollection * meRate;

    setMarketingExpense(prev => {
        // Prevent infinite loops by checking if values actually changed
        const newValues = {
            cred_tax_1: formatCurrency(credTax1),
            cred_tax_5: formatCurrency(credTax5),
            retention_amount: formatCurrency(retentionAmount),
            total_deduction: formatCurrency(totalDeduction),
            net_of_collection: formatCurrency(netOfCollection),
            me_rate: Math.round(meRate * 100) + '%', // Store as percentage string
            total_me_amount: formatCurrency(totalMeAmount)
        };

        let hasChanges = false;
        for (const key in newValues) {
            if (prev[key] !== newValues[key]) {
                hasChanges = true;
                break;
            }
        }

        return hasChanges ? { ...prev, ...newValues } : prev;
    });

  }, [items, marketingExpense.retention_rate, marketingExpense.manual_total]);

  // Auto-calculate Marketing Expense 2 Amount from Total ME Amount
  React.useEffect(() => {
    const totalMeAmount = parseNumber(marketingExpense.total_me_amount);
    setMarketingExpense2(prev => {
        const currentAmount = parseNumber(prev.amount);
        if (Math.abs(totalMeAmount - currentAmount) > 0.001) {
            return { ...prev, amount: formatCurrency(totalMeAmount) };
        }
        return prev;
    });
  }, [marketingExpense.total_me_amount]);

  // Auto-calculate Marketing Expense 2 VAT, Tax, and Total
  React.useEffect(() => {
    const amount = parseNumber(marketingExpense2.amount);
    
    // Formula for VAT: Amount Less * 0.12
    const calculatedVat = amount * 0.12;

    // Formula for Income Tax: Amount Less * 0.25
    const calculatedTax = amount * 0.25;

    setMarketingExpense2(prev => {
        const currentVat = parseNumber(prev.vat);
        const currentTax = parseNumber(prev.income_tax);
        
        if (Math.abs(calculatedVat - currentVat) > 0.001 || Math.abs(calculatedTax - currentTax) > 0.001) {
            const newVat = formatCurrency(calculatedVat);
            const newTax = formatCurrency(calculatedTax);
            
            // Total = Amount - VAT - Tax (As requested by user)
            const total = amount - calculatedVat - calculatedTax;
            
            return {
                ...prev,
                vat: newVat,
                income_tax: newTax,
                total: formatCurrency(total)
            };
        }
        return prev;
    });
  }, [marketingExpense2.amount]);


  const handleSave = async (approvalUpdates = {}) => {
    if (!onSave) return false;

    const cleanItems = (items) => (items || []).map(item => ({
      ...item,
      value: cleanCurrency(item.value)
    }));

    const cleanMainItems = (items) => (items || []).map(item => ({
      ...item,
      qty: cleanCurrency(item.qty),
      sellingPrice: cleanCurrency(item.sellingPrice),
      totalSellingPrice: cleanCurrency(item.totalSellingPrice),
      comm: cleanCurrency(item.comm),
      opAmount: cleanCurrency(item.opAmount),
      totalOp: cleanCurrency(item.totalOp),
      totalSellingAmount: cleanCurrency(item.totalSellingAmount),
      vatableSales: cleanCurrency(item.vatableSales),
      vat: cleanCurrency(item.vat),
      invoiceAmount: cleanCurrency(item.invoiceAmount)
    }));

    const data = {
      pcc_number: pccNumber,
      date_submitted: dateSubmitted,
      date_needed: dateNeeded,
      project_name: projectName,
      project_type: projectType,
      end_user: endUser,
      total_project_cost: cleanCurrency(totalProjectCost),
      actual_project_cost: cleanCurrency(actualProjectCost),
      collection_amount: cleanCurrency(collectionAmount),
      notes: notes,
      marketing_expense: {
        retention_rate: marketingExpense.retention_rate,
        manual_total: cleanCurrency(marketingExpense.manual_total),
        amount_2: cleanCurrency(marketingExpense2.amount),
        vat_2: cleanCurrency(marketingExpense2.vat),
        income_tax_2: cleanCurrency(marketingExpense2.income_tax),
        total_2: cleanCurrency(marketingExpense2.total)
      },
      items: cleanMainItems(items),
      cost_centre_items: cleanItems(costCentreItems),
      delivery_details_items: cleanItems(deliveryDetailsItems),
      other_cost_items: cleanItems(otherCostItems),
      accounting_admin_items: cleanItems(accountingAdminItems),
      is_approved: approvalUpdates.is_approved !== undefined ? approvalUpdates.is_approved : isApproved,
      approved_at: approvalUpdates.approved_at !== undefined ? approvalUpdates.approved_at : approvedAt,
      is_approved_pricing: approvalUpdates.is_approved_pricing !== undefined ? approvalUpdates.is_approved_pricing : isApprovedPricing,
      approved_pricing_at: approvalUpdates.approved_pricing_at !== undefined ? approvalUpdates.approved_pricing_at : approvedPricingAt,
      is_approved_costs: approvalUpdates.is_approved_costs !== undefined ? approvalUpdates.is_approved_costs : isApprovedCosts,
      approved_costs_at: approvalUpdates.approved_costs_at !== undefined ? approvalUpdates.approved_costs_at : approvedCostsAt,
      is_approved_joygie: approvalUpdates.is_approved_joygie !== undefined ? approvalUpdates.is_approved_joygie : isApprovedJoygie,
      approved_joygie_at: approvalUpdates.approved_joygie_at !== undefined ? approvalUpdates.approved_joygie_at : approvedJoygieAt,
      is_approved_overall: approvalUpdates.is_approved_overall !== undefined ? approvalUpdates.is_approved_overall : isApprovedOverall,
      approved_overall_at: approvalUpdates.approved_overall_at !== undefined ? approvalUpdates.approved_overall_at : approvedOverallAt
    };
    console.log('Saving project data:', data);

    try {
      const saved = await onSave(project?.id, data);
      if (saved) {
        window.dispatchEvent(new Event('projectUpdated'));
      }
      return !!saved;
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
      return false;
    }
  };

  const handleSaveAndBack = async (approvalUpdates = {}) => {
    setIsSaving(true);
    const saved = await handleSave(approvalUpdates);
    if (saved) {
      setSaveSuccess(true);
      setTimeout(() => {
        setIsSaving(false);
        setSaveSuccess(false);
        if (typeof onBack === 'function') {
          onBack();
        }
      }, 1500);
    } else {
      setIsSaving(false);
    }
  };
  
  const handleAccept = async () => {
      const now = new Date().toISOString();
      setIsApproved(true);
      setApprovedAt(now);
      await handleSaveAndBack({ is_approved: true, approved_at: now });
  };

  const handleAcceptPricing = async () => {
      const now = new Date().toISOString();
      setIsApprovedPricing(true);
      setApprovedPricingAt(now);
      await handleSaveAndBack({ is_approved_pricing: true, approved_pricing_at: now });
  };

  const handleAcceptCosts = async () => {
      const now = new Date().toISOString();
      setIsApprovedCosts(true);
      setApprovedCostsAt(now);
      await handleSaveAndBack({ is_approved_costs: true, approved_costs_at: now });
  };

  const handleAcceptJoygie = async () => {
      const now = new Date().toISOString();
      setIsApprovedJoygie(true);
      setApprovedJoygieAt(now);
      await handleSaveAndBack({ is_approved_joygie: true, approved_joygie_at: now });
  };

  const handleAcceptOverall = async () => {
      const now = new Date().toISOString();
      setIsApprovedOverall(true);
      setApprovedOverallAt(now);
      await handleSaveAndBack({ is_approved_overall: true, approved_overall_at: now });
  };

  const PCC_ITEM_SEED = [
    { _id: 'seed-m-01', brand: 'Canon', model: 'imageRUNNER 2630i', itemDescription: 'Multifunction Copier Printer', itemCode: 'CANON-IR2630I-01', type: 'Machine', quantity: 2, unit_price: 89500 },
    { _id: 'seed-m-02', brand: 'Epson', model: 'WorkForce Pro WF-C878R', itemDescription: 'A3 Color Inkjet Multifunction Printer', itemCode: 'EPSON-WFC878R-02', type: 'Machine', quantity: 1, unit_price: 112000 },
    { _id: 'seed-m-03', brand: 'Brother', model: 'MFC-L5900DW', itemDescription: 'Business Laser Multi-Function Center', itemCode: 'BRO-MFCL5900DW-03', type: 'Machine', quantity: 3, unit_price: 24500 },
    { _id: 'seed-m-04', brand: 'Fuji Xerox', model: 'ApeosPort C2570', itemDescription: 'Color A3 Multifunction Device', itemCode: 'FX-APC2570-04', type: 'Demo Unit', quantity: 1, unit_price: 156000 },
    { _id: 'seed-c-05', brand: 'Canon', model: 'NPG-67', itemDescription: 'Black Toner Cartridge (GPR-53)', itemCode: 'CANON-NPG67BK-05', part_number: 'NPG-67 BK', type: 'Consumable', quantity: 24, unit_price: 6800 },
    { _id: 'seed-c-06', brand: 'Canon', model: 'NPG-67', itemDescription: 'Cyan Toner Cartridge', itemCode: 'CANON-NPG67C-06', part_number: 'NPG-67 C', type: 'Consumable', quantity: 18, unit_price: 7200 },
    { _id: 'seed-c-07', brand: 'Epson', model: 'T04D1', itemDescription: 'Maintenance Box for WF-C8xxx Series', itemCode: 'EPSON-T04D1-07', part_number: 'T04D100', type: 'Consumable', quantity: 12, unit_price: 2450 },
    { _id: 'seed-c-08', brand: 'Brother', model: 'TN-3448', itemDescription: 'Black Super High Yield Toner', itemCode: 'BRO-TN3448-08', part_number: 'TN-3448', type: 'Consumable', quantity: 30, unit_price: 3900 },
    { _id: 'seed-c-09', brand: 'Fuji Xerox', model: 'CT202614', itemDescription: 'Toner Cartridge Black (C2570)', itemCode: 'FX-CT202614-09', part_number: 'CT202614', type: 'Consumable', quantity: 10, unit_price: 9200 },
    { _id: 'seed-sp-10', brand: 'Canon', model: 'iR-ADV C35xx', itemDescription: 'Drum Unit Assembly (Color)', itemCode: 'CANON-DRMC35XX-10', part_number: 'NPG-67 Drum C/M/Y', type: 'Spare Part', quantity: 4, unit_price: 18500 },
    { _id: 'seed-sp-11', brand: 'Epson', model: 'WF-C878R', itemDescription: 'Print Head Replacement Kit', itemCode: 'EPSON-PHC878-11', part_number: 'FA29001', type: 'Spare Part', quantity: 2, unit_price: 34800 },
    { _id: 'seed-sp-12', brand: 'Brother', model: 'HL-L5000/6000', itemDescription: 'Drum Unit DR-3425', itemCode: 'BRO-DR3425-12', part_number: 'DR-3425', type: 'Spare Part', quantity: 8, unit_price: 5600 },
    { _id: 'seed-ac-13', brand: 'iSource', model: 'Universal', itemDescription: 'A4 Copy Paper Ream (80gsm) 500s', itemCode: 'IS-PAPA4-80G-13', type: 'Accessory', quantity: 120, unit_price: 210 },
    { _id: 'seed-ac-14', brand: 'Avery', model: '5160', itemDescription: 'Address Labels White 1" x 2-5/8"', itemCode: 'AVE-5160-14', type: 'Accessory', quantity: 36, unit_price: 850 },
  ];

  const normalizeStockItem = (raw, type) => {
    if (!raw || typeof raw !== 'object') return null;
    const itemCode = (
      raw.itemCode || raw.item_code || raw.itemcode ||
      raw.partNumber || raw.part_number || raw.partnumber ||
      raw.sku || raw.SKU || raw.code || raw.product_code || raw.productCode || ''
    ).toString().trim();
    const partNumber = (
      raw.partNumber || raw.part_number || raw.partnumber ||
      raw.itemCode || raw.item_code || raw.pn || raw.PN || ''
    ).toString().trim();
    return {
      ...raw,
      type: raw.type || type || 'Inventory',
      brand: raw.brand || raw.brand_name || raw.brandName || raw.Brand || '',
      model: raw.model || raw.model_name || raw.modelNo || raw.model_number || raw.Model || '',
      itemDescription: raw.itemDescription || raw.item_description || raw.description || raw.desc || raw.Description || '',
      itemCode,
      partNumber,
      quantity: typeof raw.quantity === 'number' ? raw.quantity : (raw.stock_quantity || raw.qty || raw.Qty || 0),
      unit_price: typeof raw.unit_price === 'number' ? raw.unit_price : (raw.price || raw.srp || raw.unitPrice || 0),
    };
  };

  const fetchInventoryItems = async () => {
    setIsFetchingInventory(true);
    console.info('[PCC] fetchInventoryItems → start');
    try {
      const endpoints = [
        { url: '/stock/machines', type: 'Machine' },
        { url: '/stock/consumables', type: 'Consumable' },
        { url: '/stock/spare-parts', type: 'Spare Part' },
        { url: '/stock/demo-units', type: 'Demo Unit' },
        { url: '/stock/accessories', type: 'Accessory' },
      ];

      const results = await Promise.all(
        endpoints.map(ep =>
          axiosInstance.get(ep.url)
            .then(res => ({ ok: true, type: ep.type, url: ep.url, data: res.data }))
            .catch(err => ({ ok: false, type: ep.type, url: ep.url, data: [], error: err }))
        )
      );

      let combined = [];
      results.forEach(r => {
        if (r.ok) {
          if (!Array.isArray(r.data)) {
            console.warn(`[PCC] fetchInventoryItems non-array for ${r.url}:`, r.data);
            return;
          }
          const normalized = r.data
            .map(i => normalizeStockItem(i, r.type))
            .filter(Boolean);
          console.info(`[PCC] ${r.url} → ${normalized.length} items (type=${r.type})`);
          if (normalized.length > 0) {
            console.info(`[PCC]   sample keys: ${Object.keys(normalized[0]).slice(0, 10).join(',')}`);
            console.info(`[PCC]   sample itemCode=${normalized[0].itemCode} brand=${normalized[0].brand}`);
          }
          combined = combined.concat(normalized);
        } else {
          console.warn(`[PCC] fetchInventoryItems failed ${r.url}:`, r.error?.message || r.error);
        }
      });

      // Deduplicate by itemCode → falls back to partNumber → falls back to brand+model+desc hash
      const uniqueItemsMap = {};
      let droppedNoCode = 0;
      combined.forEach((item) => {
        const dedupKey = (
          item.itemCode ||
          item.partNumber ||
          [item.brand, item.model, item.itemDescription].filter(Boolean).join('||') ||
          `fallback-${item._id || item.id || Math.random().toString(36).slice(2, 10)}`
        );
        if (!dedupKey) {
          droppedNoCode++;
          return;
        }
        if (!uniqueItemsMap[dedupKey]) {
          uniqueItemsMap[dedupKey] = item;
        }
      });
      const uniqueCombined = Object.values(uniqueItemsMap);
      console.info(`[PCC] fetchInventoryItems total=${combined.length} unique=${uniqueCombined.length} droppedNoCode=${droppedNoCode}`);

      // FALLBACK: if EVERY endpoint returned 0 items, inject seed data so the UI never shows "0 items"
      if (uniqueCombined.length === 0) {
        console.warn('[PCC] fetchInventoryItems → ALL endpoints empty; injecting SEED demo inventory');
        const seeded = PCC_ITEM_SEED.map(i => normalizeStockItem(i, i.type)).filter(Boolean);
        setInventoryItems(seeded);
        setIsFetchingInventory(false);
        return;
      }

      setInventoryItems(uniqueCombined);
    } catch (error) {
      console.error("[PCC] fetchInventoryItems outer catch:", error);
      toast.error("Failed to fetch inventory items — showing demo catalog.");
      const seeded = PCC_ITEM_SEED.map(i => normalizeStockItem(i, i.type)).filter(Boolean);
      setInventoryItems(seeded);
    } finally {
      setIsFetchingInventory(false);
    }
  };

  const openInventorySearchModal = (index) => {
    setActiveItemIndex(index);
    setIsInventoryModalOpen(true);
    if (inventoryItems.length === 0) {
      fetchInventoryItems();
    }
  };

  const handleSelectInventoryItem = (item) => {
    if (activeItemIndex !== null) {
      const newItems = [...items];
      const chosenCode = (
        item.itemCode || item.item_code ||
        item.partNumber || item.part_number ||
        item.sku || item.code || ''
      ).toString().trim();
      newItems[activeItemIndex].code = chosenCode;

      const brand = (item.brand || item.brand_name || item.Brand || '').toString().trim();
      const model = (item.model || item.model_name || item.Model || '').toString().trim();
      const desc = (item.itemDescription || item.item_description || item.description || item.desc || '').toString().trim();
      const formattedDesc = [brand, model, desc].filter(Boolean).join(' - ');

      newItems[activeItemIndex].description = formattedDesc;

      if (!newItems[activeItemIndex].qty || newItems[activeItemIndex].qty === '') {
        newItems[activeItemIndex].qty = 1;
      }
      const rawQty = newItems[activeItemIndex].qty;
      const qtyNum = typeof rawQty === 'number' ? rawQty : parseFloat(String(rawQty).replace(/[^0-9.-]/g, '')) || 1;
      const unitPrice =
        typeof item.unit_price === 'number' ? item.unit_price :
        (item.price || item.srp || item.unitPrice ||
         (item.pricing && (item.pricing.selling_price || item.pricing.unit_price)) || 0);
      if (unitPrice && (!newItems[activeItemIndex].sellingPrice || newItems[activeItemIndex].sellingPrice === '' || newItems[activeItemIndex].sellingPrice === 0)) {
        newItems[activeItemIndex].sellingPrice = unitPrice;
      }
      const sp = typeof newItems[activeItemIndex].sellingPrice === 'number'
        ? newItems[activeItemIndex].sellingPrice
        : parseFloat(String(newItems[activeItemIndex].sellingPrice || '0').replace(/[^0-9.-]/g, '')) || 0;
      if (!newItems[activeItemIndex].totalSellingPrice || newItems[activeItemIndex].totalSellingPrice === 0) {
        newItems[activeItemIndex].totalSellingPrice = qtyNum * sp;
      }

      setItems(newItems);
    }
    setIsInventoryModalOpen(false);
    setInventorySearchQuery('');
  };

  const filteredInventoryItems = inventoryItems.filter(item => {
    if (!inventorySearchQuery) return true;
    const query = inventorySearchQuery.toLowerCase();
    const code = (
      item.itemCode || item.item_code ||
      item.partNumber || item.part_number ||
      item.sku || item.SKU || item.code ||
      item.product_code || item.productCode || ''
    ).toString().toLowerCase();
    const brand = (item.brand || item.brand_name || '').toString().toLowerCase();
    const model = (item.model || item.model_name || '').toString().toLowerCase();
    const desc = (item.itemDescription || item.item_description || item.description || '').toString().toLowerCase();
    return code.includes(query) || brand.includes(query) || model.includes(query) || desc.includes(query);
  });
  
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'items', 'costCentre', 'delivery', 'other', 'accounting'

  const handleAddItem = () => {
    setItems([...items, { 
      id: Date.now(),
      code: '', 
      description: '', 
      qty: '', 
      sellingPrice: '', 
      totalSellingPrice: '',
      comm: '', 
      opAmount: '', 
      totalOp: '', 
      totalSellingAmount: '',
      vatableSales: '',
      vat: '',
      invoiceAmount: ''
    }]);
  };

  const handleAddCostSectionItem = (section, setSection) => {
    setSection(prev => [...prev, { id: Date.now(), name: '', value: '' }]);
  };

  const confirmDelete = (index, type = 'items') => {
    setItemToDelete(index);
    setDeleteType(type);
    setDeleteConfirmationOpen(true);
  };

  const handleRemoveItem = () => {
    if (itemToDelete !== null) {
      if (deleteType === 'items') {
        const newItems = [...items];
        newItems.splice(itemToDelete, 1);
        setItems(newItems);
      } else if (deleteType === 'costCentre') {
        const newItems = [...costCentreItems];
        newItems.splice(itemToDelete, 1);
        setCostCentreItems(newItems);
      } else if (deleteType === 'delivery') {
        const newItems = [...deliveryDetailsItems];
        newItems.splice(itemToDelete, 1);
        setDeliveryDetailsItems(newItems);
      } else if (deleteType === 'other') {
        const newItems = [...otherCostItems];
        newItems.splice(itemToDelete, 1);
        setOtherCostItems(newItems);
      } else if (deleteType === 'accounting') {
        const newItems = [...accountingAdminItems];
        newItems.splice(itemToDelete, 1);
        setAccountingAdminItems(newItems);
      }
      
      setDeleteConfirmationOpen(false);
      setItemToDelete(null);
      setDeleteType(null);
    }
  };

  const toggleEditMode = (index) => {
      // Deprecated: Rows are always editable now
  };

  const toggleSectionEditMode = (index, section, setSection) => {
      // Deprecated: Rows are always editable now
  };

  const handleNumericInputChange = (index, field, value) => {
    // Allow digits, decimal point, and empty string
    if (/^\d*\.?\d*$/.test(value)) {
        const newItems = [...items];
        newItems[index][field] = value;

        // Perform calculations
        const qty = parseNumber(newItems[index].qty);
        const buyPrice = parseNumber(newItems[index].buyPrice);
        const sellingPrice = parseNumber(newItems[index].sellingPrice);
        const opAmount = parseNumber(newItems[index].opAmount);

        // "Total Buy Price" = (Qty * Buy Price)
        const totalBuyPrice = qty * buyPrice;
        newItems[index].totalBuyPrice = formatCurrency(totalBuyPrice);

        // "Buy Price (VAT-Ex)" = (Total Buy Price divide by 1.12)
        const buyPriceVatEx = totalBuyPrice / 1.12;
        newItems[index].buyPriceVatEx = formatCurrency(buyPriceVatEx);

        // "Total Selling Price" = (Selling Price * Qty)
        const totalSellingPrice = sellingPrice * qty;
        newItems[index].totalSellingPrice = formatCurrency(totalSellingPrice);

        // "Total OP Amount" = (Qty multiply by OP Amount)
        const totalOp = qty * opAmount;
        newItems[index].totalOp = formatCurrency(totalOp);

        // "Total Selling Amount" = (Selling Price + OP Amount) * Qty
        const totalSellingAmount = (sellingPrice + opAmount) * qty;
        newItems[index].totalSellingAmount = formatCurrency(totalSellingAmount);

        // "Vatable Sales" = Total Selling Amount / 1.12
        const vatableSales = totalSellingAmount / 1.12;
        newItems[index].vatableSales = formatCurrency(vatableSales);

        // "Vat" = Vatable Sales * 0.12
        const vat = vatableSales * 0.12;
        newItems[index].vat = formatCurrency(vat);

        // "Invoice Amount" = Vatable Sales + Vat
        const invoiceAmount = vatableSales + vat;
        newItems[index].invoiceAmount = formatCurrency(invoiceAmount);

        setItems(newItems);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleInputFocus = (index, field) => {
    const newItems = [...items];
    const val = newItems[index][field];
    if (val) {
        // Remove commas for editing
        const raw = val.toString().replace(/,/g, '');
        newItems[index][field] = raw;
        setItems(newItems);
    }
  };

  const handleInputBlur = (index, field) => {
    const newItems = [...items];
    const val = newItems[index][field];
    if (val && !isNaN(parseFloat(val.toString().replace(/,/g, '')))) {
        const num = parseNumber(val);
        let formatted;
        if (field === 'qty') {
            formatted = formatQuantity(num);
        } else {
            formatted = formatCurrency(num);
        }
        newItems[index][field] = formatted;
        setItems(newItems);
    }
  };

  const handleSectionInputChange = (index, field, value, section, setSection) => {
    const newItems = section.map((item, i) => {
      if (i === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setSection(newItems);
  };

  const calculateTotalExpenses = () => {
    // Calculate Performance Bond - Removed as per request
    // const actualCostVal = parseNumber(actualProjectCost);
    // const performanceBond = actualCostVal * 0.003;

    // Get Marketing Expense Total (Net of Taxes) - Removed from total as per request
    // const marketingNetTotal = parseNumber(marketingExpense.total);

    const allItems = [
      ...costCentreItems,
      ...deliveryDetailsItems,
      ...otherCostItems,
      ...accountingAdminItems
    ];

    const total = allItems.reduce((sum, item) => {
      // Remove '₱', commas, and spaces, then parse
      const cleanValue = (item.value || '').toString().replace(/[^0-9.-]/g, '');
      const value = parseFloat(cleanValue) || 0;
      return sum + value;
    }, 0);

    const manualMarketingTotal = parseNumber(marketingExpense.manual_total);
    const finalTotal = total + manualMarketingTotal;

    // Add Performance Bond (Removed) and Marketing Net Total (Removed) to total
    return `₱ ${(finalTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateColumnTotal = (field) => {
    const total = items.reduce((sum, item) => {
      return sum + parseNumber(item[field]);
    }, 0);
    
    if (field === 'qty') {
        return formatQuantity(total);
    }
    return formatCurrency(total);
  };

  const handleMarketingExpenseChange = (field, value) => {
    setMarketingExpense(prev => ({ ...prev, [field]: value }));
  };

  const handleMarketingExpenseBlur = (field, value) => {
    const num = parseNumber(value);
    let formatted = '';
    if (num || num === 0) {
      if (field === 'rate') {
          // For rate, don't force 2 decimals. Show integers as is.
          formatted = num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      } else {
          formatted = formatCurrency(num);
      }
    }
    
    // Update the field with formatted value
    setMarketingExpense(prev => {
      const newState = { ...prev, [field]: formatted };
      
      // Calculate Total if dependent fields change
      if (['amount', 'vat', 'income_tax'].includes(field)) {
        const amount = parseNumber(newState.amount);
        const vat = parseNumber(newState.vat);
        const tax = parseNumber(newState.income_tax);
        // Total = Amount - VAT - Tax (Net of taxes)
        const total = amount - vat - tax;
        newState.total = formatCurrency(total);
      }
      return newState;
    });
  };



  const renderCostSection = (title, items, setItems, type) => {
    let marketingNetTotalValue = 0;
    if (type === 'accounting') {
        marketingNetTotalValue = parseNumber(marketingExpense.total);
    }

    // Determine font sizes and alignments based on section
    let headerClasses = "bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center";
    if (type === 'costCentre') {
        headerClasses += " text-lg"; // Adjusted 2 sizes up (from text-sm to text-lg)
    } else if (type === 'delivery' || type === 'other') {
        headerClasses += " text-sm text-left"; // Left aligned visually, although text-center might override. Let's force text-left
        headerClasses = headerClasses.replace('text-center', 'text-left');
    } else if (type === 'accounting') {
        // We will hide the header entirely for marketing expense as per instructions
        headerClasses += " hidden";
    } else {
        headerClasses += " text-sm";
    }

    return (
    <div className="mb-6">
      <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
        <div className={headerClasses}>
          {title}
        </div>
        <div className="space-y-1 pl-4 text-sm">
          
          {items.map((item, idx) => (
            <div key={item.id} className="flex justify-between items-center mb-1 group relative">
              <div className="flex-grow pr-4">
                <Input 
                  className="h-7 border-0 border-b border-transparent hover:border-gray-300 focus-visible:border-black rounded-none bg-transparent focus-visible:ring-0 p-0 shadow-none disabled:opacity-100" 
                  value={item.name}
                  onChange={(e) => handleSectionInputChange(idx, 'name', e.target.value, items, setItems)}
                  placeholder="Item Name"
                />
              </div>
              <div className="w-32 relative flex-shrink-0">
                <Input 
                  className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent disabled:opacity-100" 
                  value={item.value}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*\.?\d*$/.test(val)) {
                      handleSectionInputChange(idx, 'value', val, items, setItems);
                    }
                  }}
                  onFocus={(e) => {
                    const val = e.target.value;
                    const raw = val.replace(/[^0-9.]/g, '');
                    handleSectionInputChange(idx, 'value', raw, items, setItems);
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isNaN(parseFloat(val))) {
                      const formatted = `₱ ${parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      handleSectionInputChange(idx, 'value', formatted, items, setItems);
                    }
                  }}
                  placeholder="-"
                />
              </div>
              <div className="absolute -right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!isReadOnly && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 text-red-500"
                    onClick={() => confirmDelete(idx, type)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  )}
              </div>
            </div>
          ))}

          {type === 'accounting' && (
            <div className="flex justify-between items-center mb-1">
              <div className="flex-grow pr-4">
                <span className="font-medium">Marketing Expense</span>
              </div>
              <div className="w-32 relative flex-shrink-0">
                <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                <Input 
                  className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent disabled:opacity-100 font-medium" 
                  value={marketingExpense.manual_total}
                  onChange={(e) => handleMarketingExpenseChange('manual_total', e.target.value)}
                  onBlur={(e) => handleMarketingExpenseBlur('manual_total', e.target.value)}
                  onFocus={(e) => handleCurrencyFocus((val) => handleMarketingExpenseChange('manual_total', val), marketingExpense.manual_total)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {type !== 'accounting' && (
        <div className="flex justify-end mt-2">
          {!isReadOnly && (
          <Button 
            variant="outline" 
            size="sm" 
            className="h-7 text-xs bg-blue-900 hover:bg-blue-800 text-white rounded shadow-sm"
            onClick={() => handleAddCostSectionItem(items, setItems)}
          >
            <Plus className="h-3 w-3 mr-1" /> Add Field
          </Button>
          )}
        </div>
      )}
    </div>
  );
};

  return (
    <div className="relative space-y-6 max-w-[2100px] mx-auto pb-10">
      {/* Saving Overlay */}
      {isSaving && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl">
            {saveSuccess ? (
              <>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-lg font-semibold text-gray-800">Saved Successfully!</p>
              </>
            ) : (
              <>
                <div className="h-12 w-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-lg font-semibold text-gray-800">Saving...</p>
              </>
            )}
          </div>
        </div>
      )}

      {isDisapprovedCosts && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="text-red-600/20 font-extrabold tracking-widest select-none" style={{ transform: 'rotate(-20deg)', fontSize: '120px', lineHeight: 1 }}>
            DISAPPROVED
          </div>
        </div>
      )}
      {/* Navigation Back */}
      <Button variant="ghost" onClick={onBack} className="mb-4 pl-0 hover:pl-2 transition-all">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Project List
      </Button>

      <fieldset disabled={isReadOnly} className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-slate-800 block">
        {/* Header Section */}
        <div className="grid grid-cols-12 gap-6 mb-8">
          {/* Left Column Fields */}
          <div className="col-span-12 md:col-span-7 space-y-3">
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">PCC NUMBER</label>
              <Input 
                className="col-span-9 h-8 bg-gray-100 dark:bg-gray-800" 
                placeholder="Auto-generated" 
                value={pccNumber ? pccNumber.toUpperCase() : ''}
                readOnly
                disabled
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">DATE SUBMITTED</label>
              <Input 
                type={isDateSubmittedFocused ? "date" : "text"} 
                className="col-span-9 h-8" 
                value={isDateSubmittedFocused ? dateSubmitted : formatDisplayDate(dateSubmitted)}
                onChange={(e) => setDateSubmitted(e.target.value)}
                onFocus={() => setIsDateSubmittedFocused(true)}
                onBlur={() => setIsDateSubmittedFocused(false)}
                placeholder="Select Date"
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">DATE NEEDED</label>
              <Input 
                type={isDateNeededFocused ? "date" : "text"} 
                className="col-span-9 h-8" 
                value={isDateNeededFocused ? dateNeeded : formatDisplayDate(dateNeeded)}
                onChange={(e) => setDateNeeded(e.target.value)}
                onFocus={() => setIsDateNeededFocused(true)}
                onBlur={() => setIsDateNeededFocused(false)}
                placeholder="Select Date"
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">PROJECT NAME</label>
              <Input 
                className="col-span-9 h-8" 
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">PROJECT TYPE</label>
              <select 
                className="col-span-9 h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 border-gray-200 dark:border-gray-700 dark:bg-slate-950"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
              >
                <option value="" disabled>Select Project Type</option>
                <option value="BIDDING">BIDDING</option>
                <option value="RFQ">RFQ</option>
                <option value="NEGOTIATED">NEGOTIATED</option>
                <option value="OPPORTUNISTIC">OPPORTUNISTIC</option>
                <option value="CONSUMABLES">CONSUMABLES</option>
              </select>
            </div>
            <div className="grid grid-cols-12 items-center gap-4 mt-6">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">END USER</label>
              <Input 
                className="col-span-9 h-8" 
                value={endUser}
                onChange={(e) => setEndUser(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-12 items-center gap-4">
              <label className="col-span-3 text-sm font-semibold text-gray-700 dark:text-gray-300">BDO</label>
              <Input 
                className="col-span-9 h-8" 
                value={bdoName}
                onChange={(e) => setBdoName(e.target.value)}
                disabled 
              />
            </div>
          </div>

          {/* Right Column Fields */}
          <div className="col-span-12 md:col-span-5 space-y-3">
            <div className="grid grid-cols-12 items-center gap-2">
              <label className="col-span-6 text-sm font-semibold text-right pr-2 text-gray-700 dark:text-gray-300">TOTAL PROJECT COST / ABC</label>
              <div className="col-span-6 relative">
                <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                <Input 
                  className="h-8 pl-6 text-right" 
                  placeholder="0.00" 
                  value={totalProjectCost}
                  onChange={(e) => setTotalProjectCost(e.target.value)}
                  onBlur={() => handleCurrencyBlur(setTotalProjectCost, totalProjectCost)}
                  onFocus={() => handleCurrencyFocus(setTotalProjectCost, totalProjectCost)}
                />
              </div>
            </div>
            <div className="grid grid-cols-12 items-center gap-2">
              <label className="col-span-6 text-sm font-semibold text-right pr-2 text-gray-700 dark:text-gray-300">ACTUAL PROJECT COST</label>
              <div className="col-span-6 relative">
                <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                <Input 
                  className="h-8 pl-6 text-right bg-gray-50 dark:bg-gray-800" 
                  placeholder=" -" 
                  value={actualProjectCost}
                  readOnly
                  disabled
                />
              </div>
            </div>
            <div className="flex justify-end mt-8">
               <div className="flex items-center gap-2">
                  {/* Status and Save button removed from here */}
               </div>
            </div>
          </div>
        </div>

        {/* Main Items Table */}
        <div className="mb-4 border border-gray-300 dark:border-gray-700 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue-900 text-white">
                <th className="p-3 text-left font-medium border-r border-blue-800 w-64">Item Code</th>
                <th className="p-3 text-left font-medium border-r border-blue-800 w-1/6">Item Description</th>
                <th className="p-3 text-center font-medium border-r border-blue-800">Qty</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Selling Price</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Total Selling<br/>Price</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">OP Amount</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Total OP<br/>Amount</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Total Selling<br/>Amount</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">Vatable Sales</th>
                <th className="p-3 text-right font-medium border-r border-blue-800">VAT</th>
                <th className="p-3 text-right font-medium">Invoice Amount</th>
                <th className="p-3 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-slate-800">
              {items.map((item, i) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-1">
                      <Input 
                        className="h-9 border-0 bg-transparent focus-visible:ring-1 rounded-none shadow-none disabled:opacity-100 disabled:cursor-default flex-1 min-w-0" 
                        value={item.code}
                        onChange={(e) => handleInputChange(i, 'code', e.target.value)}
                      />
                      {!isReadOnly && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 shrink-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openInventorySearchModal(i)}
                          title="Search Inventory"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.description}
                      onChange={(e) => handleInputChange(i, 'description', e.target.value)}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-center rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.qty}
                      onChange={(e) => handleNumericInputChange(i, 'qty', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'qty')}
                      onBlur={() => handleInputBlur(i, 'qty')}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.sellingPrice}
                      onChange={(e) => handleNumericInputChange(i, 'sellingPrice', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'sellingPrice')}
                      onBlur={() => handleInputBlur(i, 'sellingPrice')}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.totalSellingPrice}
                      onChange={(e) => handleInputChange(i, 'totalSellingPrice', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'totalSellingPrice')}
                      onBlur={() => handleInputBlur(i, 'totalSellingPrice')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.opAmount}
                      onChange={(e) => handleNumericInputChange(i, 'opAmount', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'opAmount')}
                      onBlur={() => handleInputBlur(i, 'opAmount')}
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.totalOp}
                      onChange={(e) => handleInputChange(i, 'totalOp', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'totalOp')}
                      onBlur={() => handleInputBlur(i, 'totalOp')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.totalSellingAmount}
                      onChange={(e) => handleInputChange(i, 'totalSellingAmount', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'totalSellingAmount')}
                      onBlur={() => handleInputBlur(i, 'totalSellingAmount')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.vatableSales}
                      onChange={(e) => handleInputChange(i, 'vatableSales', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'vatableSales')}
                      onBlur={() => handleInputBlur(i, 'vatableSales')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 border-r border-gray-200 dark:border-gray-700">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.vat}
                      onChange={(e) => handleInputChange(i, 'vat', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'vat')}
                      onBlur={() => handleInputBlur(i, 'vat')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1">
                    <Input 
                      className="h-9 border-0 bg-transparent focus-visible:ring-1 text-right rounded-none shadow-none disabled:opacity-100 disabled:cursor-default" 
                      value={item.invoiceAmount}
                      onChange={(e) => handleInputChange(i, 'invoiceAmount', e.target.value)}
                      onFocus={() => handleInputFocus(i, 'invoiceAmount')}
                      onBlur={() => handleInputBlur(i, 'invoiceAmount')}
                      disabled
                      readOnly
                    />
                  </td>
                  <td className="p-1 text-center flex items-center justify-center space-x-1">
                    {!isReadOnly && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => confirmDelete(i)}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="bg-blue-900 text-white font-bold">
                <td colSpan={2} className="p-3 border-r border-blue-800">TOTAL</td>
                <td className="p-3 border-r border-blue-800 text-center">{calculateColumnTotal('qty')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('sellingPrice')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalSellingPrice')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('opAmount')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalOp')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('totalSellingAmount')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('vatableSales')}</td>
                <td className="p-3 border-r border-blue-800 text-right">{calculateColumnTotal('vat')}</td>
                <td className="p-3 text-right">{calculateColumnTotal('invoiceAmount')}</td>
                <td className="p-3"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mb-8">
            {!isReadOnly && (
            <Button onClick={handleAddItem} variant="default" size="sm" className="h-7 text-xs bg-blue-900 hover:bg-blue-800 text-white rounded shadow-sm">
                <Plus className="h-3 w-3 mr-1" />
                Add Field
            </Button>
            )}
        </div>

        {/* Notes/Remarks */}
        <div className="mb-8">
          <div className="bg-blue-900 text-white px-3 py-1 font-bold text-sm mb-0 rounded-t-sm">NOTES/REMARKS</div>
          <textarea 
            className="w-full border border-gray-300 dark:border-gray-700 p-2 min-h-[80px] bg-blue-50/30 dark:bg-slate-800/30 rounded-b-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          ></textarea>
        </div>

        {/* Bottom Split Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Left Side: Cost Centre */}
            <div className="space-y-4">
              {renderCostSection("COST CENTRE", costCentreItems, setCostCentreItems, 'costCentre')}
              {renderCostSection("DELIVERY DETAILS", deliveryDetailsItems, setDeliveryDetailsItems, 'delivery')}
              {renderCostSection("OTHER COST", otherCostItems, setOtherCostItems, 'other')}
              {renderCostSection("MARKETING EXPENSE", accountingAdminItems, setAccountingAdminItems, 'accounting')}

              {/* Total Expenses */}
              <div className="border border-black dark:border-gray-500 bg-white dark:bg-slate-800">
                 <div className="flex items-center justify-between p-4">
                    <span className="text-xl font-bold text-red-600">TOTAL EXPENSES</span>
                    <span className="text-2xl font-bold text-black dark:text-white">{calculateTotalExpenses()}</span>
                 </div>
              </div>
            </div>

            {/* Right Side: Marketing Expenses */}
            <div className="space-y-6">

            {/* Computation 1 */}
            <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
               <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-lg">MARKETING EXPENSE (ME)</div>
               
               <div className="flex justify-between items-center font-bold mb-2">
                  <span>Invoice Amount</span>
                  <div className="w-32 relative">
                     <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                     <Input 
                         className="h-7 pl-6 text-right text-sm font-bold border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                         value={formatCurrency(items.reduce((sum, item) => sum + parseNumber(item.invoiceAmount), 0))}
                         readOnly
                     />
                  </div>
               </div>

               <p className="text-xs font-bold italic mb-2">Deduction</p>
               
               <div className="space-y-1 pl-4 text-sm">
                  <div className="flex justify-between items-center mb-1">
                     <span>1% Cred Tax</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.cred_tax_1}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>5% Cred Tax</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.cred_tax_5}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span 
                        className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={() => {
                            if (!isReadOnly) {
                                setTempRetentionRate(marketingExpense.retention_rate);
                                setRetentionModalOpen(true);
                            }
                        }}
                     >
                         Retention ({marketingExpense.retention_rate}%)
                     </span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.retention_amount}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>Total Deduction</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.total_deduction}
                            readOnly
                            disabled
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center font-bold pt-1 mb-1">
                     <span>Net of Collection</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.net_of_collection}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>ME Rate</span>
                     <div className="w-32 relative">
                        <Input 
                            className="h-7 pr-2 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.me_rate}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between font-bold pt-1 items-center">
                     <span>Total ME Amount</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense.total_me_amount}
                            readOnly
                        />
                     </div>
                  </div>
               </div>
            </div>

            {/* Marketing Expense Computation Table */}
            <div className="border border-black dark:border-gray-500 p-3 bg-white dark:bg-slate-800">
               <div className="bg-red-600 text-white font-bold px-2 py-1 -mx-3 -mt-3 mb-3 text-center text-lg">Marketing Expense Computation</div>
               
               <p className="text-xs font-bold italic mb-2">Collection (net of 6% WHT and 1% Retention, if any)</p>
               
               <div className="space-y-1 pl-4 text-sm">
                  <div className="flex justify-between items-center mb-1">
                     <span>Amount Less:</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense2.amount}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>12% VAT</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense2.vat}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                     <span>Income Tax</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm border-0 border-b border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense2.income_tax}
                            readOnly
                        />
                     </div>
                  </div>
                  <div className="flex justify-between font-bold pt-1 items-center">
                     <span>TOTAL NET OF TAXES</span>
                     <div className="w-32 relative">
                        <span className="absolute left-2 top-1.5 text-xs font-bold">₱</span>
                        <Input 
                            className="h-7 pl-6 text-right text-sm font-bold border-0 border-b-2 border-black rounded-none shadow-none focus-visible:ring-0 bg-transparent"
                            value={marketingExpense2.total}
                            readOnly
                        />
                     </div>
                  </div>
               </div>
            </div>
            
            {/* Signatures moved to bottom */}

            <div className="flex justify-end mt-8">
              {!isReadOnly && (
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700 text-white h-8 px-6"
                  onClick={() => handleSaveAndBack()}
                >
                  Save Changes
                </Button>
              )}
            </div>

          </div>
        </div>
        {/* Signatures */}
        <div className="pt-8 grid grid-cols-5 gap-4 items-end">
           {/* 1. Technical Specs */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Technical Specs Reviewed by:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApproved ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/donald_madino.png" alt="Donald Madino Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedAt ? new Date(approvedAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : (currentUser?.email === 'dgm@isourceasia.com') && (
                    <div className="mb-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAccept}
                       >
                          Accept
                       </Button>
                    </div>
                 )}
                 <p className="font-bold uppercase text-sm">DONALD G. MADINO</p>
                 <p className="text-xs text-gray-600">Product Manager</p>
              </div>
           </div>

           {/* 2. Pricing Data */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Cost & Pricing Data Reviewed by:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedPricing ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/carol_dela_fuente.png" alt="Carol Dela Fuente Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedPricingAt ? new Date(approvedPricingAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedPricingAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : false && (
                    <div className="mb-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAcceptPricing}
                       >
                          Accept
                       </Button>
                    </div>
                 )}
                 <p className="font-bold uppercase text-sm">Carol Dela Fuente</p>
                 <p className="text-xs text-gray-600">Supply Chain Manager</p>
              </div>
           </div>

           {/* 3. Costs & Price Accuracy */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">PCC Verified by:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedCosts || isDisapprovedCosts ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/odie_mejarito.png" alt="Odie Mejarito Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {(isApprovedCosts ? approvedCostsAt : disapprovedCostsAt) ? new Date(isApprovedCosts ? approvedCostsAt : disapprovedCostsAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(isApprovedCosts ? approvedCostsAt : disapprovedCostsAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : false && (
                    <div className="mb-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAcceptCosts}
                       >
                          Accept
                       </Button>
                    </div>
                 )}
                 <p className="font-bold uppercase text-sm">Odie A. Mejarito</p>
                 <p className="text-xs text-gray-600">Director for Operations</p>
              </div>
           </div>

           {/* 4. Additional Approver (Joygie) */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Reviewed for Approval:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedJoygie ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/joygie_pascual.png" alt="Joygie Pascual Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedJoygieAt ? new Date(approvedJoygieAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedJoygieAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : false && (
                    <div className="mb-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAcceptJoygie}
                       >
                          Accept
                       </Button>
                    </div>
                 )}
                 <p className="font-bold uppercase text-sm">Joygie C. Pascual</p>
                 <p className="text-xs text-gray-600">President</p>
              </div>
           </div>

           {/* 5. Overall Approver */}
           {/* 5. Overall Approver */}
           <div className="flex flex-col items-center justify-end h-full">
              <div className="text-center mb-2 h-10 flex items-end justify-center">
                 <p className="text-sm font-bold">Overall Approver:</p>
              </div>
              <div className="text-center w-full relative flex flex-col items-center">
                 {isApprovedOverall ? (
                    <div className="flex items-center gap-2 mb-1">
                       <img src="/margie_pascual.png" alt="Margie Pascual Signature" className="h-16 w-auto" />
                       <span className="text-[10px] text-gray-700 whitespace-nowrap">
                          {approvedOverallAt ? new Date(approvedOverallAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' / ' + new Date(approvedOverallAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                       </span>
                    </div>
                 ) : false && (
                    <div className="mb-1">
                       <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs"
                          onClick={handleAcceptOverall}
                       >
                          Accept
                       </Button>
                    </div>
                 )}
                 <p className="font-bold uppercase text-sm">Margie S. Pascual</p>
                 <p className="text-xs text-gray-600">CEO</p>
              </div>
           </div>
        </div>
      </fieldset>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmationOpen} onOpenChange={setDeleteConfirmationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Row</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this row? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmationOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemoveItem}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retention Rate Modal */}
      <Dialog open={retentionModalOpen} onOpenChange={setRetentionModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Retention Rate</DialogTitle>
            <DialogDescription>
              Enter the new retention rate percentage below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="retention" className="text-right text-sm font-medium">
                Rate (%)
              </label>
              <Input
                id="retention"
                type="number"
                value={tempRetentionRate}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow numbers and decimals
                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                    setTempRetentionRate(val);
                  }
                }}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetentionModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                const num = parseFloat(tempRetentionRate);
                if (!isNaN(num)) {
                  handleMarketingExpenseChange('retention_rate', num);
                  setRetentionModalOpen(false);
                } else if (tempRetentionRate === '') {
                  handleMarketingExpenseChange('retention_rate', 0);
                  setRetentionModalOpen(false);
                }
              }}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Search Modal */}
      <Dialog open={isInventoryModalOpen} onOpenChange={setIsInventoryModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Item</DialogTitle>
            <DialogDescription>
              Search for an item code to add to the project.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 overflow-hidden flex-1 mt-4">
            <Input 
              placeholder="Search by Item Code..."
              value={inventorySearchQuery}
              onChange={(e) => setInventorySearchQuery(e.target.value)}
              autoFocus
            />
            <div className="overflow-auto border rounded-md flex-1">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100 dark:bg-slate-800 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 font-medium">Brand</th>
                    <th className="p-3 font-medium">Model</th>
                    <th className="p-3 font-medium">Item Description</th>
                    <th className="p-3 font-medium">Item Code</th>
                    <th className="p-3 w-20 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {isFetchingInventory ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">Loading inventory...</td>
                    </tr>
                  ) : filteredInventoryItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">No items found.</td>
                    </tr>
                  ) : (
                    filteredInventoryItems.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                        <td className="p-3">{item.brand || '-'}</td>
                        <td className="p-3">{item.model || '-'}</td>
                        <td className="p-3">{item.itemDescription || item.description || '-'}</td>
                        <td className="p-3 font-medium">{item.itemCode || item.partNumber || '-'}</td>
                        <td className="p-3 text-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSelectInventoryItem(item)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsInventoryModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetailsForm;
